import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'

/*
 * Modal state contract — session-only by design.
 *
 * `ModalProvider` keeps the active modal kind and its context (dealId, invoiceId,
 * initialTab, etc.) entirely in React state. It is intentionally NOT synced to the
 * URL — neither `window.location.search` nor the query portion of the HashRouter
 * hash, nor any router state.
 *
 * Consequences (all expected, not bugs):
 *   - A page refresh closes any open modal and returns the user to the deals list.
 *   - Opening the same URL in a new tab shows the deals list with no modal.
 *   - Browser back / forward does not open or restore a modal.
 *
 * Why this is acceptable: product has confirmed that modals in the deal
 * management workflow are always button-triggered from the list. There is no
 * "share a modal link with a teammate" use case today.
 *
 * When to revisit: if product introduces a deep-link share requirement (e.g.
 * "send a colleague the URL of an open EditDealModal"), reopen this decision in
 * a dedicated change. Any URL-sync design must also account for `FilterProvider`,
 * which already owns its own URL-adjacent state, so the two must agree on a
 * shared query-string scheme under HashRouter before either is added.
 */

export type EditDealTab = 'general' | 'lineitems' | 'shipping'

export type ModalDescriptor =
  | {
      kind: 'edit-deal'
      /** Required when editing an existing deal; omitted for the New Deal flow. */
      dealId?: string
      isNew?: boolean
      initialTab?: EditDealTab
      highlightShipmentId?: string
    }
  | { kind: 'first-invoice'; dealId: string }
  | { kind: 'invoice-preview'; dealId: string; invoiceId: string }
  | { kind: 'send-invoice'; dealId: string; invoiceId: string }
  | { kind: 'cancel-subscription'; dealId: string }
  | {
      kind: 'confirm'
      title: string
      message: string
      confirmLabel?: string
      danger?: boolean
      onConfirm: () => void
    }

interface ModalContextValue {
  current: ModalDescriptor | null
  open: (descriptor: ModalDescriptor) => void
  close: () => void
  openSendInvoice: (dealId: string, invoiceId: string) => void
  openInvoicePreview: (dealId: string, invoiceId: string) => void
  openFirstInvoice: (dealId: string) => void
}

const ModalContext = createContext<ModalContextValue | null>(null)

export const ModalProvider: React.FunctionComponent = (props) => {
  const [current, setCurrent] = useState<ModalDescriptor | null>(null)
  const open = useCallback((descriptor: ModalDescriptor) => setCurrent(descriptor), [])
  const close = useCallback(() => setCurrent(null), [])
  const openSendInvoice = useCallback(
    (dealId: string, invoiceId: string) => setCurrent({ kind: 'send-invoice', dealId, invoiceId }),
    [],
  )
  const openInvoicePreview = useCallback(
    (dealId: string, invoiceId: string) =>
      setCurrent({ kind: 'invoice-preview', dealId, invoiceId }),
    [],
  )
  const openFirstInvoice = useCallback(
    (dealId: string) => setCurrent({ kind: 'first-invoice', dealId }),
    [],
  )
  const value = useMemo(
    () => ({ current, open, close, openSendInvoice, openInvoicePreview, openFirstInvoice }),
    [current, open, close, openSendInvoice, openInvoicePreview, openFirstInvoice],
  )
  return <ModalContext.Provider value={value}>{props.children}</ModalContext.Provider>
}

export function useModal(): ModalContextValue {
  const ctx = useContext(ModalContext)
  if (!ctx) throw new Error('useModal must be used inside <ModalProvider>')
  return ctx
}
